// CodeUploadPanel.jsx
"use client";
import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Upload, AlertCircle, CheckCircle, RefreshCw, Usb } from "lucide-react";

/* ---------------------------- ESM loader (CDN) ---------------------------- */
async function loadEsptoolESM() {
  const urls = [
    "https://esm.sh/esptool-js@0.5.5",
    "https://esm.sh/esptool-js@latest",
    "https://cdn.jsdelivr.net/npm/esptool-js/+esm",
  ];

  let mod = null;
  let lastErr = null;
  for (const url of urls) {
    try {
      // @vite-ignore: keep bundler from pre-bundling the remote ESM
      // eslint-disable-next-line no-await-in-loop
      mod = await import(/* @vite-ignore */ url);
      break;
    } catch (e) {
      lastErr = e;
    }
  }
  if (!mod) throw lastErr || new Error("Failed to load esptool-js ESM");

  const ESPLoader = mod.ESPLoader || mod.default?.ESPLoader;
  const TransportWebSerial =
    mod.TransportWebSerial ||
    mod.default?.TransportWebSerial ||
    // Some browser builds expose the WebSerial class as `Transport`
    mod.Transport ||
    mod.default?.Transport;

  if (!ESPLoader || !TransportWebSerial) {
    throw new Error("esptool-js ESM loaded but missing ESPLoader/TransportWebSerial");
  }
  return { ESPLoader, TransportWebSerial };
}

/* -------- Robust transport open: supports multiple esptool-js builds ------ */
async function createAndOpenWebSerialTransport(TransportWebSerial, setStatus) {
  // Build the transport WITHOUT a port first; let it manage the picker if it can.
  setStatus("Creating WebSerial transport...");
  const transport = new TransportWebSerial();

  // (A) Preferred: transport drives device selection
  if (typeof transport.requestPort === "function") {
    setStatus("Requesting USB device access...");
    await transport.requestPort(); // stores the picked port inside the transport
  }

  // (B) Open the port. Different builds use different signatures.
  setStatus("Opening serial port...");
  if (typeof transport.open === "function") {
    try {
      // Common signature
      await transport.open(115200);
    } catch {
      // Some builds accept an options object (note 'baudrate' vs 'baudRate')
      try {
        await transport.open({ baudRate: 115200 });
      } catch {
        await transport.open({ baudrate: 115200 });
      }
    }
  }

  // (C) If we still don’t have an open port, do a manual fallback.
  // Some builds don’t implement requestPort/open and expect you to provide a port.
  // @ts-ignore – we’re probing shape at runtime
  if (!transport.port || (transport.port && transport.port.readable == null)) {
    if ("serial" in navigator) {
      const picked = await navigator.serial.requestPort();
      // Open the raw serial port
      await picked.open({ baudRate: 115200 });

      // Try to inject into transport (varies by build)
      if (typeof transport.setPort === "function") {
        transport.setPort(picked);
      } else {
        // Last resort mutation: not ideal, but works for some builds
        // @ts-ignore
        transport.port = picked;
      }
    }
  }

  // Sanity check: ensure the transport now has a port object
  // @ts-ignore
  if (!transport.port) throw new Error("Could not open Web Serial port (no port bound)");

  return transport;
}

const CodeUploadPanel = () => {
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const isUploadStatusError = useMemo(() => uploadStatus.startsWith("error:"), [uploadStatus]);
  const isUploadStatusSuccess = useMemo(() => uploadStatus.startsWith("success:"), [uploadStatus]);

  const supportsWebSerial = typeof navigator !== "undefined" && "serial" in navigator;
  const isSecure =
    typeof window !== "undefined" &&
    (window.isSecureContext || window.location.hostname === "localhost");

  const msgOf = (err) => (typeof err === "string" ? err : err?.message || "USB flash failed");

  const uploadBinaryViaUSB = async () => {
    /** Keep refs for cleanup if the build exposes them */
    let transport = null;
    // Some builds expose .disconnect(); raw port lives inside transport.port

    try {
      if (!isSecure) throw new Error("Use HTTPS or http://localhost to enable Web Serial.");
      if (!supportsWebSerial) throw new Error("Web Serial not available. Use Chrome/Edge desktop.");

      setUploadStatus("Preparing firmware upload...");
      setUploadProgress(5);

      // 1) Fetch firmware (ensure /ez_control.bin is being served)
      const binResponse = await fetch("/ez_control.bin", { cache: "no-cache" });
      if (!binResponse.ok) throw new Error("Could not load /ez_control.bin");
      const firmwareU8 = new Uint8Array(await binResponse.arrayBuffer());

      // 2) Load esptool-js (ESM)
      setUploadStatus("Loading USB flashing tools (ESM)...");
      const { ESPLoader, TransportWebSerial } = await loadEsptoolESM();

      // 3) Create & open transport robustly (avoids getInfo on undefined)
      transport = await createAndOpenWebSerialTransport(TransportWebSerial, setUploadStatus);

      // 4) Connect to bootloader
      setUploadStatus("Connecting to bootloader...");
      const esploader = new ESPLoader(transport, 115200, (m) => console.debug("[esptool]", m));
      await esploader.connect();

      // 5) Load stub
      setUploadStatus("Loading stub...");
      await esploader.loadStub();

      // 6) Detect chip (best-effort)
      let chip = "";
      try {
        chip =
          (await esploader.chipName?.()) ||
          esploader.chip?.CHIP_NAME ||
          esploader.chip?.name ||
          "";
      } catch {
        /* ignore */
      }
      const isESP32 = /ESP32/i.test(chip);
      const chipLabel = chip || (isESP32 ? "ESP32 (heuristic)" : "ESP8266/Unknown");
      setUploadStatus(`Detected ${chipLabel}. Flashing firmware...`);

      // 7) Choose segments
      const segments = isESP32
        ? [{ data: firmwareU8, address: 0x10000 }] // ESP32 app @ 0x10000
        : [{ data: firmwareU8, address: 0x00000 }]; // ESP8266 common single image

      // 8) Flash with progress
      await esploader.flash(segments, (percent) => {
        const p = Math.max(5, Math.min(99, Math.round(percent)));
        setUploadProgress(p);
        setUploadStatus(`Flashing... ${p}%`);
      });

      setUploadProgress(100);
      setUploadStatus("success: Flash complete! Rebooting device...");

      try {
        await esploader.hardReset?.();
      } catch {}
    } finally {
      // Cleanup: try to release the port gracefully
      try {
        if (transport && typeof transport.disconnect === "function") {
          await transport.disconnect();
        }
      } catch {}
      try {
        // @ts-ignore
        const port = transport?.port;
        if (port && typeof port.close === "function") {
          await port.close();
        }
      } catch {}
    }
  };

  const handleUpload = async () => {
    if (isUploading) {
      setUploadStatus("error: Upload already in progress");
      return;
    }
    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatus("Starting USB flash...");

    try {
      await uploadBinaryViaUSB();
    } catch (err) {
      console.error("USB flash error:", err);
      setUploadStatus(`error: ${msgOf(err)}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
        <h2 className="text-2xl font-bold mb-2">USB Serial Firmware Upload</h2>
        <p className="text-gray-400">Uploads /ez_control.bin to your NodeMCU/ESP board over USB</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/5 rounded-lg p-6 mb-6"
      >
        <h3 className="text-lg font-semibold mb-4">Upload ez_control.bin via USB</h3>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <Usb className="w-4 h-4" />
            <span>File: /ez_control.bin</span>
          </div>
          <button
            onClick={handleUpload}
            disabled={isUploading}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            <span>{isUploading ? "Flashing..." : "Upload via USB"}</span>
          </button>
        </div>

        {!isSecure && (
          <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-300">
            This page is not secure. Use HTTPS (or http://localhost) to enable Web Serial.
          </div>
        )}
        {isSecure && !supportsWebSerial && (
          <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-300">
            Your browser does not support Web Serial API. Use Chrome/Edge on desktop.
          </div>
        )}
      </motion.div>

      {uploadProgress > 0 && (
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-400 mb-2">
            <span>Progress</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div
              className="bg-primary-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {uploadStatus && (
        <div
          className={`p-4 rounded-lg ${
            isUploadStatusError
              ? "bg-red-500/20 border border-red-500/50 text-red-400"
              : isUploadStatusSuccess
              ? "bg-green-500/20 border border-green-500/50 text-green-400"
              : "bg-blue-500/20 border border-blue-500/50 text-blue-400"
          }`}
        >
          <div className="flex items-center space-x-2">
            {isUploadStatusError ? <AlertCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
            <span>{uploadStatus.replace(/^(success|error):\s*/, "")}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CodeUploadPanel;
